import { useMutation, useLazyQuery, useQuery } from '@apollo/client';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Spinner, Table, Button } from 'react-bootstrap';
import moment from 'moment';

import { AccordionProps, Mission, MonthlyMissionsDisplayProps, MonthlyMissionsProgressResultProps } from '../typedefs';
import {
  FORCE_CLAIM_MISSION,
  FORCE_REWARDS_FOR_MP_LEADERBOARD,
  RESET_MISSION_PROGRESS,
  RESET_SINGLE_MISSION_PROGRESS,
  SHOW_MISSION_PROGRESS,
} from '../../../graphql';

import './MissionDisplay.css';
import {
  ForceGrantMpLeaderboardRewardsMutation,
  ForceGrantMpLeaderboardRewardsMutationVariables,
} from '../../../../../../__gqltypes__';

const monthlyPassLBRelease = moment('2025-10-01').valueOf();

const MonthlyMissionsDisplay: React.FC<MonthlyMissionsDisplayProps> = ({ userId, missions, monthId, hasMonths }) => {
  const [resetMissionProgress, { loading: resetLoading }] = useMutation(RESET_MISSION_PROGRESS);
  const [forceGetRewardMut, { loading: forceGetRewardLoading }] = useMutation<
    ForceGrantMpLeaderboardRewardsMutation,
    ForceGrantMpLeaderboardRewardsMutationVariables
  >(FORCE_REWARDS_FOR_MP_LEADERBOARD);
  const [showMissionProgress, { data, loading: showMissionLoading }] = useLazyQuery(SHOW_MISSION_PROGRESS);

  const [perviousMonthIdSelected, selectPreviousMonthId] = useState<null | string>(null);
  const [collapse, activeCollapse] = useState(false);

  const reset = useCallback(() => {
    resetMissionProgress({
      variables: {
        input: {
          missionSetUserId: missions?.id,
        },
      },
    });
  }, [resetMissionProgress, missions?.id]);

  const forceGetReward = useCallback(() => {
    forceGetRewardMut({
      variables: {
        input: {
          userId,
          monthId,
        },
      },
    });
  }, [forceGetRewardMut, userId, monthId]);

  const refetchProgress = useCallback(() => {
    showMissionProgress({ variables: { input: { monthId, userId } } });
  }, [showMissionProgress, monthId, userId]);

  if (!missions) {
    return <>No missions have been configured for this monthly pass</>;
  }

  return (
    <>
      <div className="d-flex justify-content-start align-items-baseline mb-3">
        <Button
          className="mr-2"
          disabled={!hasMonths}
          size="sm"
          onClick={() => {
            showMissionProgress({
              variables: { input: { monthId, userId } },
            });
            if (monthId === perviousMonthIdSelected) {
              activeCollapse(!collapse);
            } else {
              activeCollapse(true);
            }
            selectPreviousMonthId(monthId);
          }}
        >
          Show progress
        </Button>
        <Button
          size="sm"
          variant="danger"
          onClick={forceGetReward}
          disabled={forceGetRewardLoading || moment(monthId).startOf('month').isBefore(monthlyPassLBRelease)}
        >
          <i className="fas fa-gift mr-2" />
          Force get rewards from leaderboard
        </Button>
      </div>

      <Button size="sm" variant="danger" onClick={reset} disabled={resetLoading || !missions}>
        Reset progress
      </Button>
      <Accordion active={collapse}>
        <div className="mt-2" style={{ maxHeight: '50vh', overflowY: 'auto' }}>
          <MissionProgressResults
            missions={data?.app?.getUserMonthlyPass?.userMonthlyPass?.allMissions}
            loading={showMissionLoading}
            onChanged={refetchProgress}
          />
        </div>
      </Accordion>
    </>
  );
};

export default MonthlyMissionsDisplay;

const MissionProgressResults: React.FC<MonthlyMissionsProgressResultProps> = ({ missions, loading, onChanged }) => {
  const displayMissions = useCallback(
    (title: string, data: Mission[] | null) => {
      if (!data || !data.length) {
        return (
          <>
            <h3>{title} missions</h3>
            <div>Empty Array</div>;
          </>
        );
      }
      return (
        <>
          <h3>{title} missions</h3>
          <Table striped bordered hover size="sm">
            <thead>
              <tr>
                <th>Task name</th>
                <th>Start</th>
                <th>End</th>
                <th>Reward xp</th>
                <th>Progress</th>
                <th>Claimed date</th>
                <th>Claimed multiplier</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.map((mission: Mission) => (
                <MissionRow key={mission.id} mission={mission} onChanged={onChanged} />
              ))}
            </tbody>
          </Table>
        </>
      );
    },
    [onChanged],
  );

  if (loading) {
    return <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />;
  }

  if (!missions) {
    return <div>No result</div>;
  }

  return (
    <div>
      {displayMissions('Daily', missions?.dailyMissions)}
      <br />
      {displayMissions('Monthly', missions?.monthlyMissions)}
      <br />
      {displayMissions('Extra', missions?.extraMissions)}
      <br />
      {displayMissions('Team', missions?.teamMissions)}
    </div>
  );
};

const MissionRow: React.FC<{ mission: Mission; onChanged: () => void }> = ({ mission, onChanged }) => {
  const [forceClaim, { loading: claiming }] = useMutation(FORCE_CLAIM_MISSION, { onCompleted: onChanged });
  const [resetMission, { loading: resetting }] = useMutation(RESET_SINGLE_MISSION_PROGRESS, { onCompleted: onChanged });

  const isCompleted = !!mission.completedDate;
  const isClaimed = !!mission.claimedDate;
  const canForceClaim = isCompleted && !isClaimed;
  const canReset = mission.progress > 0 || isCompleted || isClaimed;
  const busy = claiming || resetting;

  const onForceClaim = () => {
    const taskName = mission?.mission?.tasks[0]?.type ?? 'this mission';
    if (!window.confirm(`Force claim reward for "${taskName}" for this user? Test/debug only — bypasses the normal claim flow.`)) {
      return;
    }
    forceClaim({ variables: { input: { missionProgressId: mission.id } } });
  };

  const onReset = () => {
    const taskName = mission?.mission?.tasks[0]?.type ?? 'this mission';
    if (
      !window.confirm(
        `Reset "${taskName}" for this user back to not-started (progress, completed and claimed status)? Test/debug only.`,
      )
    ) {
      return;
    }
    resetMission({ variables: { input: { missionProgressId: mission.id } } });
  };

  return (
    <tr>
      <td>{mission?.mission?.tasks[0]?.type}</td>
      <td>{moment(mission?.mission?.start).format('YYYY-MM-DD')}</td>
      <td>{moment(mission?.mission?.end).format('YYYY-MM-DD')}</td>
      <td>{mission?.mission?.rewardXp}</td>
      <td>
        {mission?.progress}/{mission.progressGoal}
      </td>
      <td>{mission?.claimedDate ? moment(mission?.claimedDate).format('YYYY-MM-DD hh:mm:ss a') : 'not claimed'}</td>
      <td>{mission?.claimedMultiplier ? (mission.claimedMultiplier > 1 ? 'X' : '-') : 'not claimed'}</td>
      <td>
        <div className="d-flex" style={{ gap: 4 }}>
          <Button
            size="sm"
            variant="danger"
            disabled={!canForceClaim || busy}
            title={isClaimed ? 'Already claimed' : !isCompleted ? 'Mission not completed yet' : undefined}
            onClick={onForceClaim}>
            {claiming ? <Spinner animation="border" size="sm" /> : 'Force claim'}
          </Button>
          <Button
            size="sm"
            variant="warning"
            disabled={!canReset || busy}
            title={!canReset ? 'Nothing to reset' : 'Resets progress, completed and claimed status for this mission only'}
            onClick={onReset}>
            {resetting ? <Spinner animation="border" size="sm" /> : 'Reset'}
          </Button>
        </div>
      </td>
    </tr>
  );
};

const Accordion: React.FC<AccordionProps> = ({ children, active }) => {
  return <div className={`mission_pannel_collapse ${active ? 'active' : ''}`}>{children}</div>;
};
